import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useNavigate } from "react-router-dom";
import {
  getTenantBranding,
  saveTenantBranding,
  uploadTenantBrandingAsset,
} from "../api/agentApi";
import {
  DEFAULT_TENANT_BRANDING,
  TenantBranding,
  brandColorAlpha,
  getBrandAppBarColor,
  getBrandContrastText,
  isValidBrandColor,
  normalizeTenantBranding,
} from "../branding";
import { APP_LAYOUT, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { useTenantBranding } from "../context/TenantBrandingContext";
import { PERMISSIONS } from "../permissions";
import ConfigSectionToggle from "./ConfigSectionToggle";
import { useUser } from "./UserContext.jsx";

const BRANDING_FIELD_SX = {
  "& .MuiFilledInput-root": {
    borderRadius: UI_RADIUS.input,
  },
};

const ASSET_UPLOAD_BUTTON_SX = {
  height: 40,
  minHeight: 40,
  mt: { md: 1 },
  flexShrink: 0,
  alignSelf: "flex-start",
};

const UNSAVED_BRANDING_DIALOG_TITLE = "Unsaved branding changes";

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const valid = isValidBrandColor(value);
  const pickerLabel = `Pick ${label.toLowerCase()}`;
  const pickerValue = valid ? value : "#000000";

  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        component="label"
        title={pickerLabel}
        sx={{
          position: "relative",
          width: 46,
          height: 46,
          flexShrink: 0,
          border: "1px solid",
          borderColor: valid ? "divider" : "error.main",
          borderRadius: UI_RADIUS.input,
          bgcolor: "background.paper",
          cursor: "pointer",
          overflow: "hidden",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          "&:focus-within": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="input"
          type="color"
          aria-label={pickerLabel}
          value={pickerValue}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            clipPath: "inset(50%)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        />
        <Box
          data-testid="visible-color-swatch"
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 4,
            borderRadius: "7px",
            bgcolor: valid ? value : "transparent",
            backgroundImage: valid
              ? "none"
              : "repeating-linear-gradient(45deg, rgba(0,0,0,0.12) 0, rgba(0,0,0,0.12) 4px, transparent 4px, transparent 8px)",
          }}
        />
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            right: 3,
            bottom: 3,
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.86)",
            color: "#17100B",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.22)",
          }}
        >
          <PaletteOutlinedIcon sx={{ fontSize: 12 }} />
        </Box>
      </Box>
      <TextField
        fullWidth
        label={label}
        value={value}
        error={Boolean(value) && !valid}
        helperText={Boolean(value) && !valid ? "Use a 6-digit hex value like #B87333." : " "}
        onChange={(event) => onChange(event.target.value)}
        sx={BRANDING_FIELD_SX}
      />
    </Stack>
  );
}

function BrandingPreview({
  branding,
  logoUrl,
}: {
  branding: TenantBranding;
  logoUrl: string | null;
}) {
  const [previewFieldValue, setPreviewFieldValue] = useState("Tenant configuration");

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: UI_RADIUS.panel,
        overflow: "hidden",
        bgcolor: "background.paper",
        height: "100%",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2.5 }, pb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Live preview
        </Typography>
      </Box>
      <Box
        data-testid="live-preview-app-bar"
        sx={{
          bgcolor: getBrandAppBarColor(branding),
          color: "#fff",
          height: APP_LAYOUT.appBarHeight,
          minHeight: APP_LAYOUT.appBarHeight,
          maxHeight: APP_LAYOUT.appBarHeight,
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.6 }}>
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={`${branding.brandName} logo preview`}
              sx={{
                maxWidth: 240,
                maxHeight: branding.showPoweredByNextMoca ? 48 : 58,
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Typography sx={{ fontWeight: 900, fontSize: 22, lineHeight: 1.1 }}>
              {branding.brandName}
            </Typography>
          )}
          {branding.showPoweredByNextMoca && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.72)",
                fontSize: "0.78rem",
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              powered by Next Moca
            </Typography>
          )}
        </Box>
      </Box>
      <Stack spacing={3.25} sx={{ p: { xs: 2.5, md: 3.25 } }}>
        <Stack spacing={0.65}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "text.primary" }}>
            Brand surface
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Buttons, fields, and signals inherit the selected tenant colors.
          </Typography>
        </Stack>

        <Stack spacing={2.75}>
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              disableElevation
              sx={{
                bgcolor: branding.primaryColor,
                color: getBrandContrastText(branding.primaryColor),
                "&:hover": {
                  bgcolor: branding.primaryColor,
                },
              }}
            >
              Primary action
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: branding.secondaryColor,
                color: branding.secondaryColor,
                "&:hover": {
                  borderColor: branding.secondaryColor,
                  bgcolor: brandColorAlpha(branding.secondaryColor, 0.08),
                },
              }}
            >
              Secondary
            </Button>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              label="Sample text field"
              value={previewFieldValue}
              size="small"
              fullWidth
              onChange={(event) => setPreviewFieldValue(event.target.value)}
              sx={BRANDING_FIELD_SX}
            />
            <Chip
              size="small"
              label="Ready"
              sx={{
                bgcolor: brandColorAlpha(branding.primaryColor, 0.13),
                color: branding.primaryColor,
                fontWeight: 900,
                flexShrink: 0,
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            />
          </Stack>
          <Box
            data-testid="accent-color-preview"
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1.25,
              p: 1.5,
              borderRadius: UI_RADIUS.input,
              border: "1px solid",
              borderColor: brandColorAlpha(branding.accentColor, 0.28),
              borderLeft: "4px solid",
              borderLeftColor: branding.accentColor,
              bgcolor: brandColorAlpha(branding.accentColor, 0.1),
              minWidth: 0,
            }}
          >
            <Chip
              size="small"
              label="Info highlight"
              sx={{
                bgcolor: branding.accentColor,
                color: getBrandContrastText(branding.accentColor),
                fontWeight: 900,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                minWidth: 0,
                lineHeight: 1.45,
              }}
            >
              Connector ready for this tenant.
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function BrandingConfigPage() {
  const { refreshBranding, resolveBrandAssetUrl } = useTenantBranding();
  const navigate = useNavigate();
  const { user, can } = useUser() || {};
  const isBrandingAdmin = Boolean(can?.(PERMISSIONS.CONFIG_BRANDING_MANAGE));
  const [branding, setBranding] = useState<TenantBranding>(DEFAULT_TENANT_BRANDING);
  const [savedBranding, setSavedBranding] = useState<TenantBranding>(DEFAULT_TENANT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<"logo" | "favicon" | "">("");
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && !isBrandingAdmin) {
      navigate("/config", { replace: true });
    }
  }, [isBrandingAdmin, navigate, user]);

  useEffect(() => {
    if (!isBrandingAdmin) {
      setLoading(false);
      return;
    }

    let active = true;
    async function loadBranding() {
      setLoading(true);
      setError("");
      try {
        const response = await getTenantBranding();
        const normalized = normalizeTenantBranding(response?.tenant_branding);
        if (active) {
          setBranding(normalized);
          setSavedBranding(normalized);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : "Unable to load tenant branding.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    loadBranding();
    return () => {
      active = false;
    };
  }, [isBrandingAdmin]);

  const normalizedBranding = useMemo(() => normalizeTenantBranding(branding), [branding]);
  const hasInvalidColors = ["primaryColor", "secondaryColor", "accentColor"].some(
    (key) => !isValidBrandColor(branding[key as keyof TenantBranding]),
  );
  const hasChanges = JSON.stringify(normalizedBranding) !== JSON.stringify(savedBranding);
  const shouldConfirmLeave = hasChanges && !saving;
  const logoPreviewUrl = resolveBrandAssetUrl(normalizedBranding.logoUrl);

  const openLeaveDialog = useCallback((onLeave: () => void) => {
    setPendingNavigation(() => onLeave);
    setLeaveDialogOpen(true);
    return false;
  }, []);

  const confirmSectionNavigation = useCallback((nextRoute: string) => {
    if (!shouldConfirmLeave) {
      return true;
    }
    return openLeaveDialog(() => navigate(nextRoute));
  }, [navigate, openLeaveDialog, shouldConfirmLeave]);

  useEffect(() => {
    if (!shouldConfirmLeave) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldConfirmLeave]);

  useEffect(() => {
    if (!shouldConfirmLeave) {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const targetElement = event.target instanceof Element ? event.target : null;
      const anchor = targetElement?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target || anchor.hasAttribute("download")) {
        return;
      }

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const isSamePage =
        nextUrl.origin === currentUrl.origin &&
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash;
      if (isSamePage) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openLeaveDialog(() => {
        if (nextUrl.origin === currentUrl.origin) {
          navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
          return;
        }
        window.location.assign(nextUrl.href);
      });
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [navigate, openLeaveDialog, shouldConfirmLeave]);

  const handleStayOnPage = useCallback(() => {
    setLeaveDialogOpen(false);
    setPendingNavigation(null);
  }, []);

  const handleLeaveWithoutSaving = useCallback(() => {
    const continueNavigation = pendingNavigation;
    setLeaveDialogOpen(false);
    setPendingNavigation(null);
    continueNavigation?.();
  }, [pendingNavigation]);

  const saveBrandingChanges = useCallback(async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const payload = normalizeTenantBranding(branding);
      const response = await saveTenantBranding(payload);
      const saved = normalizeTenantBranding(response?.tenant_branding ?? payload);
      setBranding(saved);
      setSavedBranding(saved);
      await refreshBranding();
      setMessage("Branding saved.");
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save branding.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [branding, refreshBranding]);

  const handleSaveAndLeave = useCallback(async () => {
    const continueNavigation = pendingNavigation;
    const saved = await saveBrandingChanges();
    if (!saved) {
      return;
    }

    setLeaveDialogOpen(false);
    setPendingNavigation(null);
    continueNavigation?.();
  }, [pendingNavigation, saveBrandingChanges]);

  const updateBranding = <K extends keyof TenantBranding>(key: K, value: TenantBranding[K]) => {
    setMessage("");
    setError("");
    setBranding((current) => ({ ...current, [key]: value }));
  };

  const handleAssetUpload = async (kind: "logo" | "favicon", file?: File) => {
    if (!file) {
      return;
    }
    setUploadingKind(kind);
    setMessage("");
    setError("");
    try {
      const result = await uploadTenantBrandingAsset(kind, file);
      updateBranding(kind === "logo" ? "logoUrl" : "faviconUrl", result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to upload ${kind}.`);
    } finally {
      setUploadingKind("");
    }
  };

  const handleSave = () => {
    void saveBrandingChanges();
  };

  if (!isBrandingAdmin) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "background.default",
        px: PAGE_SHELL.paddingX,
        py: PAGE_SHELL.paddingY,
      }}
    >
      <Box sx={{ maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
          <Box>
            <Typography component="h1" sx={PAGE_TYPOGRAPHY.heading}>
              Branding
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
              Configure the tenant logo, favicon, colors, and optional Next Moca attribution.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              "& .MuiButton-root": {
                width: { xs: "100%", sm: "auto" },
              },
            }}
          >
            <ConfigSectionToggle
              activeSection="branding"
              onBeforeNavigate={confirmSectionNavigation}
            />
            <Button
              variant="contained"
              disableElevation
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={loading || saving || !hasChanges || hasInvalidColors}
            >
              {saving ? "Saving..." : hasChanges ? "Save changes" : "Save branding"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltOutlinedIcon />}
              onClick={() => setBranding(DEFAULT_TENANT_BRANDING)}
              disabled={loading || saving}
            >
              Reset defaults
            </Button>
          </Stack>
        </Stack>

        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Dialog
          open={leaveDialogOpen}
          onClose={handleStayOnPage}
          aria-labelledby="branding-leave-dialog-title"
        >
          <DialogTitle id="branding-leave-dialog-title">
            {UNSAVED_BRANDING_DIALOG_TITLE}
          </DialogTitle>
          <DialogContent>
            <Typography color="text.secondary">
              Save your branding changes before leaving, or leave without saving them.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleStayOnPage}>Keep editing</Button>
            <Button color="inherit" onClick={handleLeaveWithoutSaving}>
              Leave without saving
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSaveAndLeave}
              disabled={saving || hasInvalidColors}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 55fr) minmax(0, 45fr)" },
            gap: PAGE_SHELL.sectionGap,
            alignItems: "stretch",
          }}
        >
          <Paper
            variant="outlined"
            sx={{ borderRadius: UI_RADIUS.panel, p: { xs: 2, md: 2.5 }, height: "100%" }}
          >
            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography color="text.secondary">Loading branding...</Typography>
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Brand name"
                    value={branding.brandName}
                    onChange={(event) => updateBranding("brandName", event.target.value)}
                    sx={BRANDING_FIELD_SX}
                  />
                  <FormControlLabel
                    sx={{ minWidth: { md: 260 }, alignSelf: "center" }}
                    control={
                      <Switch
                        checked={branding.showPoweredByNextMoca}
                        onChange={(event) => updateBranding("showPoweredByNextMoca", event.target.checked)}
                        inputProps={{ "aria-label": "Show powered by Next Moca" }}
                      />
                    }
                    label="Show powered by Next Moca"
                  />
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-start" }}>
                    <TextField
                      fullWidth
                      label="Logo URL"
                      value={branding.logoUrl ?? ""}
                      onChange={(event) => updateBranding("logoUrl", event.target.value || null)}
                      helperText="Paste a hosted logo URL or upload a file."
                      sx={BRANDING_FIELD_SX}
                    />
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={uploadingKind === "logo" ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon />}
                      sx={ASSET_UPLOAD_BUTTON_SX}
                      disabled={uploadingKind === "logo"}
                    >
                      Upload logo
                      <input
                        hidden
                        aria-label="Upload logo"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon"
                        onChange={(event) => {
                          handleAssetUpload("logo", event.target.files?.[0]);
                          event.target.value = "";
                        }}
                      />
                    </Button>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-start" }}>
                    <TextField
                      fullWidth
                      label="Favicon URL"
                      value={branding.faviconUrl ?? ""}
                      onChange={(event) => updateBranding("faviconUrl", event.target.value || null)}
                      helperText="Use a small .ico, .png, .jpg, or .webp icon."
                      sx={BRANDING_FIELD_SX}
                    />
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={uploadingKind === "favicon" ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon />}
                      sx={ASSET_UPLOAD_BUTTON_SX}
                      disabled={uploadingKind === "favicon"}
                    >
                      Upload favicon
                      <input
                        hidden
                        aria-label="Upload favicon"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon"
                        onChange={(event) => {
                          handleAssetUpload("favicon", event.target.files?.[0]);
                          event.target.value = "";
                        }}
                      />
                    </Button>
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PaletteOutlinedIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Theme colors
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                      gap: 1.5,
                    }}
                  >
                    <ColorField
                      label="Primary color"
                      value={branding.primaryColor}
                      onChange={(value) => updateBranding("primaryColor", value)}
                    />
                    <ColorField
                      label="Secondary color"
                      value={branding.secondaryColor}
                      onChange={(value) => updateBranding("secondaryColor", value)}
                    />
                    <ColorField
                      label="Accent color"
                      value={branding.accentColor}
                      onChange={(value) => updateBranding("accentColor", value)}
                    />
                  </Box>
                </Stack>
              </Stack>
            )}
          </Paper>

          <BrandingPreview branding={normalizedBranding} logoUrl={logoPreviewUrl} />
        </Box>
      </Box>
    </Box>
  );
}
